import RequestMerger from './request-merger'
import {MAX_IDS_PER_GET_PRODUCTS_REQUEST} from '../constants'
import {convertSelectForGetProduct, convertSelectForGetProducts} from '../commerce-api/utils'

/**
 * A class that merges product requests.
 */
class ProductRequestMerger {
    /**
     * @param {Boolean} cache - True to use memory cache or false to disable caching. Note without caching, requests will be made for the same product multiple times.
     * @param {Function} getProduct - The function to get product, usually api.shopperProducts.getProduct
     * @param {Function} getProducts - The function to get products, usually api.shopperProducts.getProducts
     * @param {Object} extraParams - Extra parameters to pass to the getProducts function
     * @param {number} [throttleInterval=0] - The interval to throttle requests
     */
    constructor({cache = true, getProduct, getProducts, extraParams, throttleInterval = 0}) {
        this._cache = cache ? new Map() : null
        const getProductSelect = convertSelectForGetProduct(extraParams?.select)
        const getProductsSelect = convertSelectForGetProducts(extraParams?.select)
        this._requestMerger = new RequestMerger({
            throttleInterval: throttleInterval,
            onExecute: async (ids) => {
                // workaround for getProducts not returning setProducts
                if (getProduct && ids.length === 1) {
                    return {
                        [ids[0]]: await getProduct({
                            parameters: {
                                ...extraParams,
                                ...(getProductSelect ? {select: getProductSelect} : {}),
                                id: ids[0]
                            }
                        })
                    }
                }

                const requests = []
                // need to split into multiple requests as the api has limit of 23 ids simultaneously
                for (let i = 0; i < ids.length; i += MAX_IDS_PER_GET_PRODUCTS_REQUEST) {
                    const chunk = ids.slice(i, i + MAX_IDS_PER_GET_PRODUCTS_REQUEST)
                    requests.push(
                        Promise.resolve(
                            getProducts({
                                parameters: {
                                    ...extraParams,
                                    ...(getProductsSelect ? {select: getProductsSelect} : {}),
                                    ids: chunk.join(',')
                                }
                            })
                        ).catch((error) => {
                            console.error(
                                `Failed to get products for ids: ${chunk.join(',')}`,
                                error
                            )
                            return Promise.reject(error)
                        })
                    )
                }

                const responses = await Promise.allSettled(requests)

                // convert the array of products from all successful responses to a map of products for faster splitting
                return Object.fromEntries(
                    responses
                        .filter((response) => response.status === 'fulfilled')
                        .flatMap((response) => response.value?.data)
                        .filter(Boolean)
                        .map((product) => [product.id, product]) || []
                )
            },
            onSplit: (response, id) => response[id]
        })
    }

    /**
     * Get a product by id
     * @param {string} productId - The product id
     * @return {Promise<Object>} - A promise that resolves with the product data
     */
    getProduct(productId) {
        if (this._cache?.has(productId)) {
            return this._cache.get(productId)
        }
        const response = this._requestMerger.queue(productId)
        if (this._cache) {
            this._cache.set(productId, response)
        }
        return response
    }

    /**
     * Get multiple products by id
     * @param {string[]} productIds - The product ids
     * @return {Promise<Object[]>} - A promise that resolves with an array of product data
     */
    getProducts(productIds) {
        return Promise.all(productIds.map((productId) => this.getProduct(productId)))
    }
}

export default ProductRequestMerger
